// chatService.js
// Sends chat messages to the Bedrock Agent via the backend Lambda.
// Manages session IDs so multi-turn conversations work correctly.
// When the agent finishes gathering information it emits a structured
// "WORKSHEET READY" signal; this service surfaces that as worksheetReady
// and worksheetParams so the caller can trigger worksheet generation.

import cognitoService from './cognitoService';
import { generateActivity } from './generatorService';

// In-memory session store keyed by a caller-supplied conversationKey.
// Using a plain object so the session survives across component re-renders
// within the same tab, but resets on a full page reload.
const _sessions = {};

/**
 * Send a chat message to the Bedrock Agent and return the reply.
 *
 * @param {string} message           - The user's message text.
 * @param {string} [conversationKey] - Optional stable key (e.g. "generate-chat")
 *                                     used to persist the sessionId across calls.
 *                                     Defaults to "default".
 * @returns {Promise<{
 *   reply:           string,
 *   sessionId:       string,
 *   sessionEnded:    boolean,
 *   worksheetReady:  boolean,
 *   worksheetParams: object|null,
 *   generation:      object|null   // result from generatorService if worksheet was auto-submitted
 * }>}
 */
export const sendChatMessage = async (message, conversationKey = 'default') => {
  if (!message || !message.trim()) {
    throw new Error('Message cannot be empty');
  }

  const token = cognitoService.getIdToken();
  if (!token) {
    throw new Error('User is not authenticated');
  }

  const apiEndpoint =
    import.meta.env.VITE_API_ENDPOINT || import.meta.env.REACT_APP_API_ENDPOINT;

  if (!apiEndpoint) {
    throw new Error('API endpoint is not configured');
  }

  // Reuse an existing sessionId if we have one for this conversation
  const existingSessionId = _sessions[conversationKey];

  const requestBody = {
    message: message.trim(),
    ...(existingSessionId ? { sessionId: existingSessionId } : {}),
  };

  const response = await fetch(`${apiEndpoint}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    mode: 'cors',
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('Authentication failed. Please log in again.');
    }
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed with status ${response.status}`);
  }

  const data = await response.json();

  // Persist the session ID for follow-up messages
  if (data.sessionId) {
    _sessions[conversationKey] = data.sessionId;
  }

  // If the agent ended the session, clear the stored ID so next call starts fresh
  if (data.sessionEnded) {
    delete _sessions[conversationKey];
  }

  const result = {
    reply: data.reply,
    sessionId: data.sessionId,
    sessionEnded: data.sessionEnded ?? false,
    worksheetReady: data.worksheetReady ?? false,
    worksheetParams: data.worksheetParams ?? null,
    generation: null,
  };

  // Auto-trigger worksheet generation when the agent signals it is ready
  if (result.worksheetReady && result.worksheetParams) {
    try {
      result.generation = await generateActivity(result.worksheetParams);
    } catch (genError) {
      // Surface the error but don't swallow the chat reply
      result.generationError = genError.message || 'Worksheet generation failed';
      console.error('chatService: worksheet generation failed', genError);
    }
  }

  return result;
};

/**
 * Explicitly reset the stored session for a given conversation key.
 * Call this when the user clicks "New conversation" or navigates away.
 *
 * @param {string} [conversationKey] - Same key used in sendChatMessage.
 */
export const resetConversation = (conversationKey = 'default') => {
  delete _sessions[conversationKey];
};

/**
 * Returns true if there is an active session already in progress.
 *
 * @param {string} [conversationKey]
 */
export const hasActiveSession = (conversationKey = 'default') => {
  return Boolean(_sessions[conversationKey]);
};

export default { sendChatMessage, resetConversation, hasActiveSession };
